/**
 * UTM attribution (RED-10693).
 *
 * Captures UTM parameters present on the landing URL and retains them for the
 * duration of the browser session so key events — particularly `purchase` —
 * can be attributed to the campaign that drove them. Session-scoped: values do
 * not persist beyond the current session and are never overwritten by a later
 * navigation that lacks UTM parameters.
 *
 * Attribution is held in memory until the customer accepts cookies, because
 * writing it to `sessionStorage` is device storage under PECR reg 6. In memory
 * it survives the whole single-page journey, just not a hard reload.
 */

import { hasCookieConsent } from '../hooks/useCookieConsent/useCookieConsent'

import { CreateOrderInput } from '@src/graphql-types'

const STORAGE_KEY = 'redbox_utm'

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const

export type UtmParams = Partial<Record<(typeof UTM_KEYS)[number], string>>

/** Reads UTM parameters from the current URL query string. */
const readUtmFromUrl = (): UtmParams => {
  const params = new URLSearchParams(window.location.search)
  const utm: UtmParams = {}
  for (const key of UTM_KEYS) {
    const value = params.get(key)
    if (value) utm[key] = value
  }
  return utm
}

/** In-memory cache — UTM is session-static, so avoid re-reading storage per event. */
let cachedUtm: UtmParams | null = null

/**
 * Captures UTM parameters from the landing URL.
 *
 * Only writes when at least one UTM parameter is present, so a later landing
 * without UTM parameters never clears an existing attribution.
 */
export const captureUtmParams = (): void => {
  const utm = readUtmFromUrl()
  if (Object.keys(utm).length === 0) return
  cachedUtm = utm
  persistUtmParams()
}

/** Promotes in-memory attribution to sessionStorage. No-op without consent. */
export const persistUtmParams = (): void => {
  if (!cachedUtm || !hasCookieConsent()) return
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cachedUtm))
  } catch {
    // sessionStorage unavailable (private mode / quota) — attribution is best-effort
  }
}

/** Returns the UTM parameters captured for the current session, if any. */
export const getUtmParams = (): UtmParams => {
  if (cachedUtm) return cachedUtm
  if (!hasCookieConsent()) return {}
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    cachedUtm = stored ? (JSON.parse(stored) as UtmParams) : {}
  } catch {
    cachedUtm = {}
  }
  return cachedUtm
}

/** UTM attribution mapped to the order input's camelCase fields. */
export type OrderUtmInput = Pick<
  CreateOrderInput,
  'utmSource' | 'utmMedium' | 'utmCampaign' | 'utmContent' | 'utmTerm'
>

/** Maps the session UTM params to the create-order input shape. */
export const getUtmForOrder = (): OrderUtmInput => {
  const utm = getUtmParams()
  return {
    utmSource: utm.utm_source,
    utmMedium: utm.utm_medium,
    utmCampaign: utm.utm_campaign,
    utmContent: utm.utm_content,
    utmTerm: utm.utm_term,
  }
}
